import { BariatricProcedureStage } from '@bariendo/lib/constants';
import { MARKET_LOCATION_EXTENSION } from '@bariendo/lib/extensions';
import { VerificationTaskStage } from '@bariendo/lib/pn-consultation-script';
import { MedplumClient } from '@medplum/core';
import { fetchBot } from '@/services/services';
import { fillIntakeSelectionIfEmpty } from './patientEmployer';

/**
 * The rest of v2.0's "Move to Financing and approval" (BAR-3002), once the
 * verification task exists: the Intake and billing Location, only where it's
 * empty (a value set by hand is never overwritten, as on save, BAR-3000), then
 * the stage through `update-procedure-status`. The open task is what moves the
 * stage, and what keeps it there through the hourly restage. Before the consult
 * has ended the stage can't move yet; the restage moves it once it has.
 * Throws when the restage wrote nothing.
 */
export async function moveToFinancingApproval(
  medplum: MedplumClient,
  patientId: string,
  market: string | undefined
): Promise<VerificationTaskStage | undefined> {
  if (market) await fillIntakeSelectionIfEmpty(medplum, patientId, MARKET_LOCATION_EXTENSION, market);
  const { procedureStage } = await fetchBot<{ procedureStage?: BariatricProcedureStage }>(
    medplum,
    'update-procedure-status',
    { patient_id: patientId }
  );
  // No stage means the bot couldn't read every fact and wrote nothing: fail, so the button stays to retry.
  if (!procedureStage) throw new Error('The patient’s stage could not be updated. Try again.');
  if (procedureStage === BariatricProcedureStage.FINANCING_APPROVAL) return 'financing';
  // Before the consult has ended the stage can't move yet.
  if (BEFORE_CONSULT_ENDS.includes(procedureStage)) return 'financing-pending';
  // Already past it (an approved case, a booked procedure): nothing to say about the stage.
  return undefined;
}

const BEFORE_CONSULT_ENDS: readonly BariatricProcedureStage[] = [
  BariatricProcedureStage.PRE_CONSULTATION_PAID,
  BariatricProcedureStage.PRE_CONSULTATION_UNPAID,
];
